/**
 * GPU time per DRAW, summed per PASS.
 *
 * The profiler used to open one `TIME_ELAPSED` query in a rAF callback and close
 * it in the next, so the query spanned the whole frame INTERVAL: the draws, the
 * compositing, the swap and the wait for vsync. On a 60 Hz display that reads
 * ~16.7 ms however little work the GPU does — a headroom metric that can never
 * show headroom. Bracketing each draw measures the work itself.
 *
 * Per PASS because that is the unit worth showing: "this shadow pass costs 4 ms"
 * is actionable, "the frame costs 16 ms" is not. It is also the unit WebGPU
 * reports natively, so both backends can feed the same view.
 *
 * `TIME_ELAPSED` queries cannot nest — which suits draws exactly, since they are
 * sequential.
 */

export interface PassTiming {
    readonly frame: number;
    readonly pass: number;
    readonly draws: number;
    readonly gpuMs: number;
}

interface QueryGL {
    QUERY_RESULT: number;
    QUERY_RESULT_AVAILABLE: number;
    createQuery(): object | null;
    deleteQuery(query: object): void;
    beginQuery(target: number, query: object): void;
    endQuery(target: number): void;
    getQueryParameter(query: object, name: number): unknown;
}

interface InFlight { query: object; frame: number; pass: number }

export class DrawTimer {

    private readonly _free: object[] = [];
    private readonly _all: object[] = [];
    private _inFlight: InFlight[] = [];
    /** frame:pass → accumulated result, in insertion order */
    private readonly _results = new Map<string, { frame: number, pass: number, draws: number, gpuMs: number }>();

    private _frame = 0;
    private _pass = 0;

    constructor(private readonly gl: QueryGL, private readonly timeElapsed: number) { }

    /** Which pass the draws that follow belong to. */
    beginPass(frame: number, pass: number): void {
        this._frame = frame;
        this._pass = pass;
    }

    /**
     * Run one draw with a query around it.
     *
     * The draw happens WHATEVER the queries do — a context that refuses to hand
     * out a query must not cost the page its frame.
     */
    timeDraw(draw: () => void): void {
        const query = this._free.pop() ?? this._create();
        if (!query) { draw(); return; }

        let started = false;
        try {
            this.gl.beginQuery(this.timeElapsed, query);
            started = true;
        }
        catch { /* another query already open, or the context went away */ }

        try { draw(); }
        finally {
            if (started) {
                try {
                    this.gl.endQuery(this.timeElapsed);
                    this._inFlight.push({ query, frame: this._frame, pass: this._pass });
                }
                catch { this._free.push(query); }
            }
            else {
                this._free.push(query);
            }
        }
    }

    /** Collect whatever the GPU has finished. Results lag a few frames — that is
     *  the nature of the query, not a bug to wait on. */
    drain(): void {
        const pending: InFlight[] = [];
        for (const entry of this._inFlight) {
            let available = false;
            try { available = this.gl.getQueryParameter(entry.query, this.gl.QUERY_RESULT_AVAILABLE) === true; }
            catch { continue; } // context lost — drop it rather than spin
            if (!available) { pending.push(entry); continue; }

            let nanos = 0;
            try { nanos = Number(this.gl.getQueryParameter(entry.query, this.gl.QUERY_RESULT)) || 0; }
            catch { nanos = 0; }
            this._add(entry.frame, entry.pass, nanos / 1e6);
            this._free.push(entry.query);
        }
        this._inFlight = pending;
    }

    /** Everything resolved so far, oldest pass first. Clears what it returns. */
    takeResults(): PassTiming[] {
        const out = [...this._results.values()].map(r => ({ ...r, gpuMs: Math.round(r.gpuMs * 1000) / 1000 }));
        this._results.clear();
        return out;
    }

    /** Queries still waiting on the GPU. */
    get pending(): number { return this._inFlight.length; }

    dispose(): void {
        for (const query of this._all) {
            try { this.gl.deleteQuery(query); } catch { /* context gone */ }
        }
        this._all.length = 0;
        this._free.length = 0;
        this._inFlight = [];
        this._results.clear();
    }

    private _create(): object | null {
        let query: object | null = null;
        try { query = this.gl.createQuery(); }
        catch { return null; }
        if (query) this._all.push(query);
        return query;
    }

    private _add(frame: number, pass: number, ms: number): void {
        const key = `${frame}:${pass}`;
        const existing = this._results.get(key);
        if (existing) {
            existing.draws++;
            existing.gpuMs += ms;
        }
        else {
            this._results.set(key, { frame, pass, draws: 1, gpuMs: ms });
        }
    }
}
